import {useCallback, useEffect, useRef, useState} from 'react';
import {Subscription} from '../service/SubscriptionService';
import {fetchCachedSubscription, peekCachedSubscription, subscribeToSubscriptionCache} from '../service/subscriptionCache';

export interface UseSubscriptionResult {
    /** Эффективная подписка текущего пользователя; `null`, пока не загружена или при ошибке. */
    subscription: Subscription | null;
    loading: boolean;
    /** Данные недоступны. Не сбой экрана: поверхность просто не показывает тарифный блок. */
    error: boolean;
    /** Повторная загрузка в обход TTL (кнопка «Повторить»). */
    reload: () => void;
}

/**
 * Тариф текущего пользователя для UI-поверхностей (CTHS-340).
 *
 * Данные берутся из общего кеша {@link fetchCachedSubscription}, поэтому шапка,
 * профиль и премиум-гейт меню обходятся одним запросом. Ошибка наружу не
 * выбрасывается: подписка — вспомогательная информация, её недоступность не должна
 * ломать навигацию (requirements FR-5, FR-14).
 *
 * @param enabled пока `false`, запрос не уходит — дропдаун грузит тариф при первом
 *                открытии, а не на каждом монтировании шапки (FR-4).
 */
export function useSubscription(enabled = true): UseSubscriptionResult {
    const [subscription, setSubscription] = useState<Subscription | null>(() => peekCachedSubscription());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    // Счётчик ручных перезагрузок: меняет зависимость эффекта, не трогая его тело.
    const [attempt, setAttempt] = useState(0);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(false);
        fetchCachedSubscription()
            .then(data => {
                if (!cancelled && mounted.current) {
                    setSubscription(data);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled && mounted.current) {
                    setSubscription(null);
                    setError(true);
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, attempt]);

    // Смена тарифа (или выход) сбрасывает общий кеш — смонтированные хуки перечитывают
    // данные сами, иначе бейдж остался бы с прежним тарифом до перезагрузки вкладки.
    useEffect(() => subscribeToSubscriptionCache(() => {
        if (mounted.current) {
            setAttempt(prev => prev + 1);
        }
    }), []);

    const reload = useCallback(() => setAttempt(prev => prev + 1), []);

    return {subscription, loading, error, reload};
}

export default useSubscription;
